<template>
  <v-layout wrap>
    <v-flex xs12>
      <v-toolbar dense extended color="primary" dark style="padding-top:50px">
        <v-flex xs12 pa-5>
          <v-toolbar-title slot="extension" class="display-1">
            <v-icon x-large style="padding-bottom:10px">assignment</v-icon>
            Job Run Detail
          </v-toolbar-title>
          <v-btn flat dark :to="{ name: 'ingestionDashboard' }"><v-icon left>arrow_back</v-icon>Back to Dashboard</v-btn>
        </v-flex>
      </v-toolbar>
    </v-flex>
    <v-flex xs12 pa-3>
      <v-card v-if="job">
        <v-card-title class="headline">
          Job {{ shorten(job.Id) }}
          <v-spacer></v-spacer>
          <v-chip :color="statusColor(job.Status)" dark>{{ job.Status }}</v-chip>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs6 sm4><strong>Job ID:</strong> {{ job.Id }}</v-flex>
            <v-flex xs6 sm4><strong>Config ID:</strong> {{ job.ConfigurationId || '-' }}</v-flex>
            <v-flex xs6 sm4><strong>Schedule:</strong> {{ job.ScheduleName || 'Manual' }}</v-flex>
            <v-flex xs6 sm4><strong>Started:</strong> {{ formatDate(job.StartedAt) }}</v-flex>
            <v-flex xs6 sm4><strong>Completed:</strong> {{ formatDate(job.CompletedAt) }}</v-flex>
            <v-flex xs6 sm4><strong>Duration:</strong> {{ formatDuration(job.DurationSeconds) }}</v-flex>
          </v-layout>
          <v-divider class="my-3"></v-divider>
          <div v-if="job.ErrorMessage">
            <v-alert color="error" icon="warning" :value="true">{{ job.ErrorMessage }}</v-alert>
          </div>
          <div v-if="job.SummaryLog">
            <v-subheader>Summary Log</v-subheader>
            <pre class="summary-json">{{ formatJson(job.SummaryLog) }}</pre>
          </div>
        </v-card-text>
      </v-card>
    </v-flex>

    <!-- Log Entries -->
    <v-flex xs12 pa-3>
      <v-card>
        <v-card-title>
          <v-subheader>Detail Log Entries</v-subheader>
          <v-spacer></v-spacer>
          <v-select label="Level" v-model="logFilter.level" :items="logLevels" clearable @change="loadEntries" style="max-width:150px"></v-select>
        </v-card-title>
        <v-card-text>
          <div v-if="logEntries.length === 0 && !entriesLoading" class="text-xs-center grey--text pa-3">
            {{ entriesError ? 'Detailed logs unavailable (Elasticsearch may be offline).' : 'No log entries found.' }}
          </div>
          <v-timeline v-else dense>
            <v-timeline-item v-for="(entry, i) in logEntries" :key="i"
              :color="entryLevelColor(entry.Level)" small>
              <v-card class="elevation-1">
                <v-card-title class="caption pa-2">
                  <v-chip :color="entryLevelColor(entry.Level)" small dark class="mr-2">{{ entry.Level }}</v-chip>
                  <span class="grey--text">{{ formatDate(entry.Timestamp) }}</span>
                  <span v-if="entry.Source" class="ml-2 grey--text">[{{ entry.Source }}]</span>
                </v-card-title>
                <v-card-text class="py-1 px-2 body-2">{{ entry.Message }}</v-card-text>
                <v-card-text v-if="entry.Exception" class="py-1 px-2">
                  <pre class="error-text">{{ entry.Exception }}</pre>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
          <v-pagination v-if="logEntriesTotalCount > logFilter.pageSize"
            v-model="logFilter.page" :length="Math.ceil(logEntriesTotalCount / logFilter.pageSize)"
            @input="loadEntries" class="mt-2"></v-pagination>
        </v-card-text>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const ingestion = createNamespacedHelpers('ingestionManagement')

export default {
  props: ['runId'],
  data() {
    return {
      job: null,
      entriesLoading: false,
      entriesError: false,
      logFilter: { level: null, page: 1, pageSize: 50 },
      logLevels: ['Information', 'Warning', 'Error'],
    }
  },
  computed: {
    ...ingestion.mapGetters(['logEntries', 'logEntriesTotalCount']),
  },
  methods: {
    ...ingestion.mapActions(['fetchJobRun', 'fetchLogEntries']),
    statusColor(s) {
      return { Queued: 'grey', Running: 'info', Completed: 'success', Failed: 'error' }[s] || 'grey'
    },
    entryLevelColor(l) {
      return { Information: 'info', Warning: 'warning', Error: 'error' }[l] || 'grey'
    },
    formatDate(d) { return d ? new Date(d).toLocaleString() : '-' },
    formatDuration(sec) {
      if (sec == null) return '-'
      const m = Math.floor(sec / 60); const s = Math.floor(sec % 60)
      return m > 0 ? `${m}m ${s}s` : `${s}s`
    },
    formatJson(str) {
      try { return JSON.stringify(JSON.parse(str), null, 2) } catch (e) { return str }
    },
    shorten(id) { return id ? id.substring(0, 8) + '...' : '' },
    loadJob() {
      this.fetchJobRun({ id: this.runId, successCb: (data) => { this.job = data; this.loadEntries() } })
    },
    loadEntries() {
      this.entriesLoading = true; this.entriesError = false
      const params = { jobRunId: this.runId, page: this.logFilter.page, pageSize: this.logFilter.pageSize }
      if (this.logFilter.level) params.level = this.logFilter.level
      this.fetchLogEntries({ params }).then(() => {
        if (this.logEntries.length === 0) this.entriesError = true
      }).catch(() => {
        this.entriesError = true
      }).finally(() => { this.entriesLoading = false })
    },
  },
  mounted() { this.loadJob() },
}
</script>

<style scoped>
.summary-json {
  background: #f5f5f5; padding: 12px; border-radius: 4px;
  font-size: 12px; max-height: 300px; overflow: auto;
}
.error-text {
  background: #fff5f5; padding: 8px; border-radius: 4px;
  font-size: 11px; color: #b9121b; max-height: 150px; overflow: auto;
}
</style>
